import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import type { PositionWithOffsetValue } from '@elfsight/configurator-contracts';
import { useDelayedUnmount } from '@elfsight/lib-utils';
import { classNames } from '@elfsight/widget-utils';
import { Portal } from '../portal';
import { getAnimationForType, getTransformOrigin } from './animation-utils';
import type {
  SlideInAnimationValue,
  SlideInCustomAnimationDirection
} from './types';

export type SlideInProps = PropsWithChildren<{
  position: PositionWithOffsetValue;
  isOpen: boolean;
  animation?: SlideInAnimationValue;
  animationSpeed?: number;
  customAnimationDirection?: SlideInCustomAnimationDirection;
  ariaLabel?: string;
  className?: string;
}>;

export const SlideIn = ({
  children,
  position,
  isOpen,
  customAnimationDirection,
  ariaLabel,
  animationSpeed = 0.35,
  animation = 'fade',
  className
}: SlideInProps) => {
  const {
    position: innerPosition,
    verticalOffset,
    horizontalOffset
  } = position;

  const [primaryPosValue, secondaryPosValue] = innerPosition.split('-');
  const animationDuration = animation === 'instant' ? 0 : animationSpeed * 1000; // Convert to ms

  const [isMounted, isClosing] = useDelayedUnmount(isOpen, animationDuration);
  const isVisible = isOpen && !isClosing;

  const primaryPosition = {
    bottom: `align-items: flex-end; justify-content: center; padding-bottom: ${verticalOffset}px;`,
    top: `align-items: flex-start; justify-content: center; padding-top: ${verticalOffset}px;`,
    right: `align-items: center; justify-content: flex-end; padding-right: ${horizontalOffset}px;`,
    left: `align-items: center; justify-content: flex-start; padding-left: ${horizontalOffset}px;`,
    center: `align-items: center; justify-content: center;`
  }[primaryPosValue];

  const secondaryPosition = {
    right: `justify-content: flex-end; padding-right: ${horizontalOffset}px;`,
    left: `justify-content: flex-start; padding-left: ${horizontalOffset}px;`
  }[secondaryPosValue];

  const combinedCSS = `
    ${primaryPosition || ''}
    ${secondaryPosition || ''}
  `;

  const animationDirection = (customAnimationDirection ||
    secondaryPosValue ||
    primaryPosValue) as SlideInCustomAnimationDirection;

  return (
    isMounted && (
      <Portal>
        <PositionContainer
          _positionCSS={combinedCSS}
          aria-label={ariaLabel}
          aria-modal
          role="dialog"
          tabIndex={-1}
          className={classNames('slide-in-position-container', {}, [className])}
        >
          <ActivatePointerEvents>
            <AnimatedContent
              _animationType={animation}
              _animationDirection={animationDirection}
              _animationDuration={animationDuration}
              _isVisible={isVisible}
              _isOpen={isOpen}
            >
              {children}
            </AnimatedContent>
          </ActivatePointerEvents>
        </PositionContainer>
      </Portal>
    )
  );
};

const PositionContainer = styled.div<{
  _positionCSS: string;
}>`
  position: fixed;
  inset: 0;
  display: flex;
  z-index: 9999999;
  pointer-events: none;
  ${({ _positionCSS }) => css`
    ${_positionCSS}
  `}
`;

const ActivatePointerEvents = styled.div`
  pointer-events: auto;
`;

const AnimatedContent = styled.div<{
  _animationType: SlideInAnimationValue;
  _animationDirection: SlideInCustomAnimationDirection;
  _animationDuration: number;
  _isVisible: boolean;
  _isOpen: boolean;
}>`
  ${({
    _animationType,
    _animationDirection,
    _animationDuration,
    _isVisible,
    _isOpen
  }) => {
    const animation = getAnimationForType(
      _animationType,
      _animationDirection,
      _isVisible
    );
    const transformOrigin = getTransformOrigin(_animationDirection);

    return css`
      ${animation &&
      css`
        animation: ${animation} ${_animationDuration}ms ease-out forwards;
      `}
      ${_animationType === 'zoom' &&
      css`
        transform-origin: ${transformOrigin};
      `}
      
      @media (prefers-reduced-motion: reduce) {
        & {
          animation: none;
          opacity: ${_isOpen ? 1 : 0};
        }
      }
    `;
  }}
`;
